import type { CSSProperties } from 'react';
import { MOODS } from '@racksavant/core';
import { Face } from './Photo';
import s from './MoodReadCard.module.css';

/**
 * An example mood read, set as a printed slip.
 *
 * The shape matches `zMoodRead` in `@racksavant/core` exactly — verdict,
 * energy 0–100, two to five adjectives, a colour story, a caption. Notice what
 * is missing: any sentence about the person. That is the whole rail.
 */
const EXAMPLE = {
  moods: ['regal', 'moody'] as const,
  verdict: 'The coat is doing the talking. Let it.',
  energy: 72,
  energyNote: 'Loud enough to be remembered, quiet enough to be believed.',
  adjectives: ['tailored', 'unhurried', 'expensive', 'faintly villainous'],
  colorStory:
    'Oxblood over charcoal, with one line of brass at the cuff doing all the work. Keep everything else matte.',
  swatches: ['#5A1220', '#22222A', '#C69A45'],
  caption: 'dressed for a room I have not walked into yet',
};

export function MoodReadCard() {
  const primary = MOODS[EXAMPLE.moods[0]];

  return (
    <article
      className={s.slip}
      style={{ '--read-color': primary.color, '--energy': `${EXAMPLE.energy}%` } as CSSProperties}
    >
      <header className={s.head}>
        <span className={s.stamp}>The mood read</span>
        <span className={s.moods}>
          {EXAMPLE.moods.map((id) => (
            <span key={id} className={s.mood}>
              <span className={s.moodSwatch} style={{ background: MOODS[id].color }} />
              {MOODS[id].label}
            </span>
          ))}
        </span>
      </header>

      <h3 className={s.verdict}>{EXAMPLE.verdict}</h3>

      <Face kind="unreal" size={78} tilt={5} className={s.sticker} />

      <div className={s.meter}>
        <div className={s.meterHead}>
          <span>Energy</span>
          <span className={s.meterValue}>{EXAMPLE.energy} / 100</span>
        </div>
        <div className={s.track} role="img" aria-label={`Energy ${EXAMPLE.energy} out of 100`}>
          <div className={s.fill} />
        </div>
        <p className={s.meterNote}>{EXAMPLE.energyNote}</p>
      </div>

      <ul className={s.adjectives}>
        {EXAMPLE.adjectives.map((word) => (
          <li key={word}>{word}</li>
        ))}
      </ul>

      <div className={s.colorRow}>
        <span className={s.swatches} aria-hidden="true">
          {EXAMPLE.swatches.map((hex) => (
            <span key={hex} className={s.swatch} style={{ background: hex }} />
          ))}
        </span>
        <p className={s.colorText}>{EXAMPLE.colorStory}</p>
      </div>

      <div className={s.caption}>
        <span className={s.captionLabel}>Caption, ready to paste</span>
        <p className={s.captionText}>{EXAMPLE.caption}</p>
      </div>
    </article>
  );
}
